/**
 * @file Defines the FacetsMobile custom element for mobile facet filtering.
 * @extends HTMLDivElement
 */

const { formatNumberWithCommas, currencyExchange } = window.BAO.Utils
class FacetsMobile extends window.BAO.CustomElement({
  SuperClass: HTMLDivElement,
}) {
  /**
   * Constructs the FacetsMobile element.
   * Initializes state, event bus, and facet key constants.
   */
  constructor () {
    super()

    switch (this.dataset.type) {
      case 'search':
        this.eventBus = window.theme.searchResults.eventBus
        this.state = {
          root: window.theme.searchResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }
        break
      case 'autocomplete':
        this.eventBus = window.theme.autocompleteResults.eventBus
        this.state = {
          root: window.theme.autocompleteResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }
        break
      default:
        // Default is collection
        this.eventBus = window.theme.category.eventBus
        this.state = {
          root: window.theme.category?.stateLayer?.state || { products: [] },
          observer: null,
        }
    }

    if (!this.eventBus) {
      console.error('Event bus not available for FacetsMobile')
    }
    if (this.state.root === undefined) {
      console.error('State root not available for FacetsMobile')
    }

    // Define constants for facet keys to avoid magic strings
    this._FACET_KEYS = Object.freeze({
      PRICE: 'klevu_price',
      TYPE: 'type',
      GENDER: 'gender',
      BRAND: 'brand',
      TAGS: 'tags',
      CONTACT_US: 'contact_us',
      BACK_IN_STOCK: 'back_in_stock',
      // Add other facet keys if necessary
    })

    this.type = this.getAttribute('data-type') || 'collection'
  }

  /**
   * Standard custom element lifecycle callback.
   * Called when the element is inserted into the DOM.
   * Subscribes to events for facet updates.
   */
  connectedCallback () {
    // If initial facets are available from the root state, assign them.
    // These will be overwritten if 'api:facets:fetched' event fires with new data.
    if (this.state.root && this.state.root.facets) {
      this.state.facets = this.state.root.facets
    }
    this.subscribeToEvents()
  }

  /**
   * Subscribes to the 'api:facets:fetched' event to update and render facets.
   */
  subscribeToEvents () {
    if (!this.eventBus) return

    let hasRendered = false

    this.eventBus.subscribe('api:facets:fetched', response => {
      this._processFetchedFacets(response)
      // this._sortFacets()

      if (this.type === 'autocomplete') {
        const openFacetLabels = this._getOpenFacetLabels()
        this._renderInitialFacetsAndSetupListeners(true)
        this._restoreOpenFacetLabels(openFacetLabels)
        return
      }

      if (!hasRendered) {
        this._renderInitialFacetsAndSetupListeners()
        hasRendered = true
      }
      // If facets can be updated dynamically after initial render and require listeners on new elements,
      // setupEventListeners might need more specific logic or event delegation.
    })
  }

  /**
   * Processes the facet data received from the API.
   * @private
   * @param {Array<Object>} response - The array of facet objects from the API.
   */
  _processFetchedFacets (response) {
    this.state.facets = Array.isArray(response) ? response : []
  }

  /**
   * Sorts the facets based on the configured filter order from `window.theme.collection.filterOrder`.
   * @private
   */
  _sortFacets () {
    if (window.theme.collection && window.theme.collection.filterOrder) {
      const filterOrder = window.theme.collection.filterOrder
        .split(',')
        .map(filter => filter.trim().toLowerCase())

      const getSortOrder = facet => {
        const index = filterOrder.indexOf(facet.label.toLowerCase())
        return index === -1 ? filterOrder.length : index
      }

      this.state.facets.sort((a, b) => {
        return getSortOrder(a) - getSortOrder(b)
      })
    }
  }

  /**
   * Renders the initial set of facets into the DOM and sets up event listeners.
   * @private
   */
  _renderInitialFacetsAndSetupListeners (shouldClear = false) {
    if (shouldClear) {
      this.innerHTML = ''
    }

    this.state.facets.forEach((facet, index) => {
      if (facet.key === this._FACET_KEYS.PRICE) {
        const priceHTML = this.buildPriceHTML(facet, index)
        if (priceHTML) {
          this.insertAdjacentHTML('beforeend', priceHTML)
        }
      } else {
        const html = this.buildHTML(facet, index)
        if (html) {
          this.insertAdjacentHTML('beforeend', html)
        }
      }
    })
    this._setupEventListeners() // Call once after initial render
  }

  _getOpenFacetLabels () {
    return Array.from(
      this.querySelectorAll('details[is="facet-summary"][open]'),
    )
      .map(detail => detail.getAttribute('data-label'))
      .filter(Boolean)
  }

  _restoreOpenFacetLabels (labels) {
    if (!labels || labels.length === 0) return

    labels.forEach(label => {
      const detail = this.querySelector(
        `details[is="facet-summary"][data-label="${label}"]`,
      )
      if (detail) {
        detail.setAttribute('open', '')
      }
    })
  }

  buildPriceHTML (facet, index) {
    const existingFacetIndex = this.state.root.activeFacets.findIndex(
      activeFacet => activeFacet.key === 'klevu_price',
    )

    const currentMinFacet = currencyExchange(
      this.state.root.activeFacets[existingFacetIndex]?.values[0],
      false,
    )
    const currentMaxFacet = currencyExchange(
      this.state.root.activeFacets[existingFacetIndex]?.values[1],
      true,
    )

    const minValueInt = facet.min

    let maxValueInt = facet.max
    let maxValue = currencyExchange(maxValueInt, true)
    let suffix = ''
    const actualMaxValue = facet.max

    if (maxValue > parseInt(this.dataset.maxPrice)) {
      maxValueInt = parseInt(this.dataset.maxPrice)
      maxValue = maxValueInt
    }

    const minValue = currencyExchange(minValueInt, false)

    const currentMinValue =
      currentMinFacet && Number(currentMinFacet) >= Number(minValue)
        ? currentMinFacet
        : minValue

    const currentMaxValue =
      currentMaxFacet && Number(currentMaxFacet) <= Number(maxValueInt)
        ? currentMaxFacet
        : maxValue

    let currentMaxInputValue = maxValue

    if (maxValue >= parseInt(this.dataset.maxPrice)) {
      suffix = '+'
      currentMaxInputValue = ''
    }

    const currencySymbol = window.theme.moneyFormat.split('{')[0]
    return `
      <details
        class="clc-ProductGridFacetMobile clc-ProductGridFacetMobile-priceRange"
        data-animate-from-right
        data-fully-open="false"
        is="facet-summary"
        data-label="${this.handleize(facet.label)}"
        style="order: ${index};"
      >
        <summary class="clc-ProductGridFacetMobile_Summary fz-15_35" data-facet-summary-el="summary">
          <span class="clc-ProductGridFacetMobile_FilterLabel">Price
            <svg class="clc-ProductGridFacetMobile_FilterLabelSvg" xmlns="http://www.w3.org/2000/svg">
              <use href="#icon-chevron-down" />
            </svg>
          </span>
        </summary>

        <div class="clc-ProductGridFacetMobile_Display" data-filter-facet-el="content" data-facet-summary-el="content">

        <header class="clc-ProductGridFacetMobile_Header">
          <button class="clc-ProductGridFacetMobile_Back" type="button" data-facet-summary-el="close">
            <svg class="clc-ProductGridFacetMobile_CloseSvg" xmlns="http://www.w3.org/2000/svg">
              <use href="#icon-arrow-small"></use>
            </svg>

            <span class="util-ScreenReaderOnly">Close</span>
          </button>

          <span class="clc-ProductGridFacetMobile_Title fz-15_35">
            Price
          </span>

          <button class="clc-ProductGridFacetMobile_Close" type="button" data-drawers-close="">
            <svg class="clc-ProductGridFacetMobile_CloseSvg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <use href="#icon-close"></use>
            </svg>

            <span class="util-ScreenReaderOnly">Close</span>
          </button>
        </header>

        <div class="clc-ProductGridFacetMobile_Body" data-range-slider-external-el="container" data-range-slider-external-el="container">
          <price-range class="prc-Range" data-type="${this.type}">
            <div class="prc-Range_Column">
              <div class="prc-Range_Field">
                <input class="prc-Range_Input" id="price-range-min" value="${formatNumberWithCommas(
                  currentMinValue,
                )}" type="text" data-range-slider-external-el="min" placeholder="${currencySymbol}${formatNumberWithCommas(
      currentMinValue,
    )}" data-range-min="${minValue}" data-range-max="${maxValue}" data-max-reached-limit="${
      this.dataset.maxPrice
    }" data-max-reached-int="${actualMaxValue}" data-price-range-el="min" />

                <label class="prc-Range_Label" for="price-range-min" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${formatNumberWithCommas(
      currentMinValue,
    )}">
                  <span class="util-ScreenReaderOnly">
                    {{ 'sections.collection_template.from' | t }}:
                    {{ cart.currency.symbol }}${formatNumberWithCommas(
                      currentMinValue,
                    )}</span>
                </label>
              </div>
            </div>

            <div class="prc-Range_Column">
              <div class="prc-Range_Field">
                <input class="prc-Range_Input" id="price-range-max" type="text" data-range-slider-external-el="max" placeholder="${currencySymbol}${formatNumberWithCommas(
      currentMaxValue,
    )}" data-range-min="${minValue}" data-range-max="${maxValue}" data-price-range-el="max"  data-max-reached-limit="${
      this.dataset.maxPrice
    }" data-max-reached-int="${actualMaxValue}" value="${currentMaxInputValue}"  />

                <label class="prc-Range_Label" for="price-range-max" data-range-slider-external-el="maxLabel" data-currency-symbol="${currencySymbol}" data-text="${currencySymbol}${formatNumberWithCommas(
      currentMaxValue,
    )}${suffix}">
                  <span class="util-ScreenReaderOnly">
                    {{ 'sections.collection_template.to' | t }}:
                    {{ cart.currency.symbol }}${formatNumberWithCommas(
                      currentMaxValue,
                    )}</span>
                </label>
              </div>
            </div>
          </price-range>

            <div class="clc-ProductGridFacet_RangeSlider">
              <range-slider data-type="${
                this.type
              }" class="rng-Slider" min="${minValue}" max="${maxValue}" min-value="${currentMinValue}" max-value="${currentMaxValue}">
                <div class="rng-Slider_Inner">
                  <button class="rng-Slider_Dot rng-Slider_Dot-left" type="button" data-range-slider-el="touchLeft">
                    <span class="rng-Slider_DotInner"></span>
                  </button>

                  <button class="rng-Slider_Dot rng-Slider_Dot-right" type="button" data-range-slider-el="touchRight" style="right: 0%;">
                    <span class="rng-Slider_DotInner"></span>
                  </button>

                  <div class="rng-Slider_Line" data-range-slider-el="line">
                    <span class="rng-Slider_LineInner"></span>
                    <span class="rng-Slider_LineHighlighter" data-range-slider-el="highlighter"></span>
                  </div>
                </div>
              </range-slider>
            </div>
          </div>
        </div>
      </details>
    `
  }

  /**
   * Sets up event listeners for facet interactions, like closing a facet detail.
   * Uses event delegation on `this` element for dynamically added content if possible,
   * or queries for specific elements if they are stable after initial render.
   * @private
   */
  _setupEventListeners () {
    this.querySelectorAll('.clc-ProductGridFacetMobile_Back').forEach(
      closeTrigger => {
        if (closeTrigger.dataset.listenerAttached) return
        closeTrigger.dataset.listenerAttached = 'true'

        closeTrigger.addEventListener('click', event => {
          event.preventDefault()
          const facetDetail = closeTrigger.closest(
            '.clc-ProductGridFacetMobile',
          )
          if (facetDetail) {
            facetDetail.removeAttribute('open')
          }
        })
      },
    )
  }

  /**
   * Builds the HTML string for a single facet.
   * @param {Object} facet - The facet object to build HTML for.
   * @param {number} index - The index of the facet, used for ordering.
   * @returns {string} The HTML string for the facet, or an empty string if the facet should not be rendered.
   */
  buildHTML (facet, index) {
    if (facet.key === this._FACET_KEYS.TYPE && !this.showProductTypeFilter) {
      return ''
    }

    if (facet.key === this._FACET_KEYS.GENDER && !this.showGenderFilter) {
      return ''
    }

    if (this.isMakerCollection && facet.key === this._FACET_KEYS.BRAND) {
      return ''
    }

    if (
      facet.key === this._FACET_KEYS.TAGS ||
      facet.key === this._FACET_KEYS.CONTACT_US ||
      facet.key === this._FACET_KEYS.BACK_IN_STOCK
    ) {
      return ''
    }

    return `
      <details
        class="clc-ProductGridFacetMobile"
        data-animate-from-right
        data-fully-open="false"
        is="facet-summary"
        data-label="${this.handleize(facet.label)}"
        style="order: ${index};"
      >
        <summary class="clc-ProductGridFacetMobile_Summary fz-15_35" data-facet-summary-el="summary">
          <span class="clc-ProductGridFacetMobile_FilterLabel">
            ${facet.label}

            <svg class="clc-ProductGridFacetMobile_FilterLabelSvg" xmlns="http://www.w3.org/2000/svg">
              <use href="#icon-chevron-down" />
            </svg>
          </span>
        </summary>
        <div class="clc-ProductGridFacetMobile_Display clc-ProductGridFacetMobile_Display-collectionTree" data-filter-facet-el="content" data-facet-summary-el="content">
          <header class="clc-ProductGridFacetMobile_Header" data-filter-facet-el="close">
            <button class="clc-ProductGridFacetMobile_Back" type="button" data-facet-summary-el="close">
              <svg class="clc-ProductGridFacetMobile_CloseSvg" xmlns="http://www.w3.org/2000/svg">
                <use href="#icon-arrow-small"></use>
              </svg>

              <span class="util-ScreenReaderOnly">{{ 'accessibility.close' | t }}</span>
            </button>

            <span class="clc-ProductGridFacetMobile_Title fz-15_35">
              ${facet.label}
            </span>

            <button class="clc-ProductGridFacetMobile_Close" type="button" data-drawers-close="">
              <svg class="clc-ProductGridFacetMobile_CloseSvg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <use href="#icon-close"></use>
              </svg>

              <span class="util-ScreenReaderOnly">{{ 'accessibility.close' | t }}</span>
            </button>
          </header>
          <div class="clc-ProductGridFacetMobile_Body" data-filter-facet-search-external-el="body">
          ${
            this.filtersWithSearch &&
            this.filtersWithSearch.includes(facet.label.toLowerCase())
              ? `
            <div class="clc-ProductGridFacetMobile_Search">
              <input class="clc-ProductGridFacetMobile_SearchInput"
                is="filter-facet-search"
                value=""
                placeholder="Search ${facet.label}"
                data-filter-facet-el="search"
              >
            </div>
            `
              : ''
          }
            <ul class="clc-ProductGridFacetMobile_List">
              ${facet.options
                .map(option => {
                  return `
                  <li class="clc-ProductGridFacetMobile_Item" data-filter-facet-search-external-el="item">
                    <label for="${this.dataset.type}-${
                    option.name
                  }" class="clc-ProductGridFacetMobile_Label fz-15_24" data-filter-facet-search-external-el="label" data-facets-list-el="item" data-facet="${
                    facet.key
                  }" data-option="${option.name}" ${
                    option.count === 0
                      ? 'aria-disabled="true"'
                      : 'aria-disabled="false"'
                  }>
                      <input
                        class="clc-ProductGridFacetMobile_Checkbox"
                        data-facets-mobile-el="item"
                        data-facet="${facet.key}"
                        type="checkbox"
                        is="facet-checkbox"
                        data-type="${this.type}"
                        value="${option.value}"
                        id="${this.dataset.type}-${option.name}"
                        ${option.selected ? 'checked' : ''}
                      >
                      <span class="clc-ProductGridFacetMobile_CheckboxIcon"></span>

                      <span class="clc-ProductGridFacetMobile_CheckboxText">
                        ${option.name}
                      </span>
                    </label>
                  </li>
                `
                })
                .join('')}
            </ul>
          </div>
        </div>

      </details>
    `
  }

  /**
   * Gets the list of filters that should have a search input.
   * The value is expected to be a comma-separated string from the attribute.
   * @returns {string | null} A string containing filter labels for search, or null if attribute is not set.
   */
  get filtersWithSearch () {
    return this.getAttribute('data-filters-with-search')
  }

  /**
   * Gets whether the product type filter should be shown.
   * @returns {boolean} True if the product type filter should be shown, false otherwise.
   */
  get showProductTypeFilter () {
    return this.getAttribute('data-product-type-metafield') === 'true'
  }

  /**
   * Gets whether the gender filter should be shown.
   * @returns {boolean} True if the gender filter should be shown, false otherwise.
   */
  get showGenderFilter () {
    return this.getAttribute('data-gender-metafield') === 'true'
  }

  /**
   * Gets whether the current collection is a "maker" collection.
   * @returns {boolean} True if it's a maker collection, false otherwise.
   */
  get isMakerCollection () {
    return this.getAttribute('data-is-maker-collection') === 'true'
  }

  /**
   * Gets the price filter range element from the DOM.
   * @returns {Element | null} The price filter DOM element, or null if not found.
   */
  get priceFilter () {
    return this.querySelector('.clc-ProductGridFacetMobile-priceRange')
  }

  handleize (str) {
    return str
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }
}

customElements.define('facets-mobile', FacetsMobile, {
  extends: 'div',
})
